<template>
  <div class="bidder-quote">
    <div class="bidder-quote-info">
      <div class="description">
        <div class="description__label">多轮报价</div>
        <div class="description__content">
          <el-scrollbar>
            <div
              v-for="quote in roundQuoteList"
              :key="quote.roundId"
              class="round-quote"
            >
              <div class="round-quote__label">第{{ quote.roundNum }}轮报价</div>
              <div class="round-quote__content">
                <span class="price">
                  {{ quote.offerPrice | priceFilter }}
                </span>
                <span>{{ offerUnit }}</span>
              </div>
            </div>
          </el-scrollbar>
        </div>
      </div>
      <div class="description">
        <div class="description__label">当前报价</div>
        <div class="description__content flex row-between">
          <div>
            <div class="text-baseline">
              <span class="price m-r-4">
                {{ bidderQuote.offerPrice | priceFilter }}
              </span>
              <span>{{ offerUnit }}</span>
            </div>
            <div v-if="offerUnit === '元'" class="text-danger">
              大写：{{ bidderQuote.offerPrice | priceInChinese }}
            </div>
          </div>
        </div>
      </div>
      <div class="description">
        <div class="description__label">当前报价时间</div>
        <div class="description__content text-main">
          {{ bidderQuote.offerTime }}
        </div>
      </div>
      <div v-if="offerRequireTax" class="description">
        <div class="description__label">未含税价</div>
        <div class="description__content flex row-between">
          <div>
            <div class="text-baseline">
              <span class="price m-r-4">
                {{ bidderQuote.noRateOfferPrice | priceFilter }}
              </span>
              <span>{{ offerUnit }}</span>
            </div>
            <div v-if="offerUnit === '元'" class="text-danger">
              大写：{{ bidderQuote.noRateOfferPrice | priceInChinese }}
            </div>
            <el-button type="text" @click="openBidderQuoteDetailDialog">
              明细
            </el-button>
          </div>
        </div>
      </div>

      <div class="description">
        <div class="description__label">企业折扣率</div>
        <div class="description__content">
          <span v-if="bidderQuote.offerRate">{{ bidderQuote.offerRate }}%</span>
          <span v-else>-</span>
        </div>
      </div>
      <div class="description">
        <div class="description__label">折后金额</div>
        <div class="description__content">
          <div>
            <span class="price m-r-4">
              {{ bidderQuote.offerRatePrice | priceFilter }}
            </span>
            <span>{{ offerUnit }}</span>
          </div>
          <div v-if="offerUnit === '元'" class="text-danger">
            大写：{{ bidderQuote.offerRatePrice | priceInChinese }}
          </div>
        </div>
      </div>
      <div class="text-tip m-b-14">
        折后金额=当前报价金额-当前报价金额*企业折扣率
      </div>
    </div>

    <div v-if="!quoteReviewOpened" class="float">
      <div class="float-head">
        <a-icon class="m-r-8" type="exclamation-circle" />
        <span class="float-head__title">未开启打分</span>
      </div>
      <div class="float-body m-t-16">
        <div class="manual">第一步：设置企业折扣率；</div>
        <div class="manual">
          第二步：发起报价，确定是最后一轮报价后，组长开启评分；
        </div>
        <div class="manual">第三步：评分。</div>
        <div class="manual m-t-24">
          提示：只要未开启打分，可以多次修改企业折扣率和发起多轮报价
          重新评审时，如果不需要再次设置企业折扣率和发起报价，可以直接开启打分。
        </div>
      </div>
    </div>

    <!-- 未含税总价明细弹窗 -->
    <material-no-rate-quote-detail-dialog
      ref="noRateQuoteDetail"
      :review-id="reviewId"
      :order-id="orderId"
      :bidder="bidder"
      :purchase-type="purchaseType"
      :offer-unit="offerUnit"
    />
  </div>
</template>

<script>
import MaterialNoRateQuoteDetailDialog from '@/components/online-review/review/MaterialNoRateQuoteDetailDialog.vue'
import { getNegotiationReviewSupplierQuoteRoundListApi } from '@/api/procure/negotiation-review'
import { strToThousands } from '@/utils/utils'
import nzhcn from 'nzh/cn'
import { currencyTypeDict, purchaseTypeDict } from '@/dictionary/project/common'
import { getInquiryReviewSupplierQuoteRoundListApi } from '@/api/procure/inquiry-review'

/**
 * 评审报价信息
 */
export default {
  name: 'OnlineReviewQuote',

  components: { MaterialNoRateQuoteDetailDialog },

  inject: ['quoteRateChange'],

  filters: {
    priceFilter(price) {
      return strToThousands(price || '0')
    },

    priceInChinese(price) {
      return nzhcn.toMoney((price || 0).toString(), { outSymbol: false })
    }
  },

  props: {
    reviewId: {
      type: [String, Number],
      default: 0
    },

    orderId: {
      type: [String, Number],
      default: 0
    },

    bidder: {
      type: Object,
      default: () => {}
    },

    offerUnit: {
      type: String,
      default: currencyTypeDict.YUAN.label
    },

    purchaseType: {
      type: [Number, String],
      default: purchaseTypeDict.INQUIRY.value
    },

    // 是否开启打分
    quoteReviewOpened: {
      type: Boolean,
      default: false
    },

    // 报价是否含税
    offerRequireTax: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 多轮报价列表
      roundQuoteList: []
    }
  },

  computed: {
    bidderQuote() {
      return this.roundQuoteList[0] || { offerPrice: 0 }
    }
  },

  watch: {
    bidder: {
      handler(val) {
        if (val?.supplierId) {
          this.getAllRoundQuoteList()
        }
      },
      deep: true,
      immediate: true
    },

    quoteRateChange: {
      handler() {
        this.getAllRoundQuoteList()
      },
      deep: true
    }
  },

  methods: {
    /**
     * 获取多轮报价
     */
    getAllRoundQuoteList() {
      const objApi = {
        [purchaseTypeDict.INQUIRY
          .value]: getInquiryReviewSupplierQuoteRoundListApi,
        [purchaseTypeDict.NEGOTIATION
          .value]: getNegotiationReviewSupplierQuoteRoundListApi
      }
      objApi[this.purchaseType](this.reviewId, {
        orderId: this.orderId,
        supplierId: this.bidder.supplierId
      })
        .then(res => {
          this.roundQuoteList = (res.data || []).reverse()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    openBidderQuoteDetailDialog() {
      this.$refs.noRateQuoteDetail.openDialog()
    }
  }
}
</script>

<style scoped lang="scss">
.bidder-quote {
  width: 94%;
  background: $white-color;
  padding-bottom: 24px;
}

.description {
  width: 100%;
  color: #999999;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  padding: 0 16px 14px 0;

  .description__label {
    width: 123px;
    flex-shrink: 0;
  }

  .description__content {
    flex: 1;
    overflow: hidden;

    .round-quote {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      flex-shrink: 0;

      &:not(:last-child) {
        margin-right: 46px;
      }

      .round-quote__label {
        color: #333333;
      }

      .round-quote__content {
      }
    }

    ::v-deep .el-scrollbar {
      width: 100%;
      //height: 100%;
    }

    ::v-deep .el-scrollbar__wrap {
      width: 100%;
      overflow: auto;
      height: 60px;
      padding-bottom: 10px;

      .el-scrollbar__view {
        display: flex;
      }
    }

    .text-baseline {
      display: flex;
      align-items: flex-end;
    }

    // 价格分
    .price-score {
      display: flex;
    }
  }

  .price {
    font-size: 16px;
    color: #ff4d4f;
  }
}

.text-tip {
  color: $third-text-color;
}

.float {
  width: 96%;
  margin: 0 auto;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.14);
  padding: 16px 37px;

  .float-head {
    width: 100%;
    text-align: center;

    .float-head__title {
      font-size: 14px;
      color: #333333;
    }
  }

  .float-body {
    width: 100%;

    .manual {
      font-size: 12px;
      color: #666666;
    }
  }
}
</style>
